import React from 'react';
import { createRoot } from 'react-dom/client';
import LoadingSpinner from '../shared/components/loading-spinner/LoadingSpinner';

// Rate limit storage key
export const RATE_LIMIT_KEY = 'app_rate_limit_until';

// Type for active rate limit violation response
export interface ActiveRateLimitViolationResponse {
    userId: string;
    occurredAtUtc: string;
    endsAtUtc: string;
}

// Check if app is currently rate limited
export const isRateLimited = (): boolean => {
    const rateLimitUntil = localStorage.getItem(RATE_LIMIT_KEY);
    if (rateLimitUntil) {
        const limitTime = new Date(rateLimitUntil);
        return new Date() < limitTime;
    }
    return false;
};

export const setRateLimit = (rateLimitUntil: Date) => {
    localStorage.setItem(RATE_LIMIT_KEY, rateLimitUntil.toISOString());
}

export const removeRateLimit = () => {
    localStorage.removeItem(RATE_LIMIT_KEY);
};

// Get remaining rate limit time in seconds
export const getRateLimitRemainingSeconds = (): number => {
    const rateLimitUntil = localStorage.getItem(RATE_LIMIT_KEY);
    if (rateLimitUntil) {
        const limitTime = new Date(rateLimitUntil);
        const now = new Date();
        return Math.max(0, Math.ceil((limitTime.getTime() - now.getTime()) / 1000));
    }
    return 0;
};

// Overlay management
let overlayRoot: any = null;
let rateLimitRoot: any = null;
let rateLimitTimer: NodeJS.Timeout | null = null;

export const showOverlay = (message: string) => {
    // Don't show loading overlay if rate limited
    if (isRateLimited()) {
        return;
    }

    // Remove existing overlay if present
    hideOverlay();

    // Create overlay element
    const overlayElement = document.createElement('div');
    overlayElement.id = 'loading-overlay';
    overlayElement.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;        
    `;

    document.body.appendChild(overlayElement);

    // Create and render LoadingSpinner
    overlayRoot = createRoot(overlayElement);
    overlayRoot.render(
        React.createElement('div', {
            className: 'flex flex-col items-center gap-4'
        }, [
            React.createElement(LoadingSpinner, {
                key: 'spinner',
                size: 'lg',
                label: message
            })
        ])
    );
};

// Forward declarations for functions used before they are defined
export const hideOverlay = () => {
    const existingOverlay = document.getElementById('loading-overlay');
    if (existingOverlay) {
        if (overlayRoot) {
            overlayRoot.unmount();
            overlayRoot = null;
        }
        existingOverlay.remove();
    }
};

// Show rate limit overlay with countdown
export const showRateLimitOverlay = () => {

    const initialSeconds = getRateLimitRemainingSeconds();

    // Check if rate limit overlay already exists and is active
    const existingOverlay = document.getElementById('rate-limit-overlay');
    if (existingOverlay && rateLimitRoot && rateLimitTimer) {
        // Overlay is already active, don't recreate it
        // console.log('Rate limit overlay already active, skipping recreation');
        return;
    }

    // Remove existing loading overlay but only hide rate limit overlay if it's not working properly
    hideOverlay();

    // Only hide rate limit overlay if there's no active timer or it's broken
    if (!rateLimitTimer || !rateLimitRoot) {
        hideRateLimitOverlay();
    }

    // Create overlay element
    const overlayElement = document.createElement('div');
    overlayElement.id = 'rate-limit-overlay';
    overlayElement.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.8);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;        
    `;

    document.body.appendChild(overlayElement);

    // Create root only once
    rateLimitRoot = createRoot(overlayElement);

    let remainingSeconds = initialSeconds;

    const renderRateLimitContent = (timeString: string) => {
        return React.createElement('div', {
            className: 'bg-white rounded p-8 max-w-md mx-4 text-center'
        }, [
            React.createElement('div', {
                key: 'icon',
                className: 'text-msi-chart-red text-6xl mb-4'
            }, '⚠️'),
            React.createElement('h2', {
                key: 'title',
                className: 'text-2xl font-bold text-gray-800 mb-4'
            }, 'Request Limit Exceeded'),
            React.createElement('p', {
                key: 'message',
                className: 'text-gray-600 mb-6'
            }, 'Too many requests have been made. Please wait before trying again.'),
            React.createElement('div', {
                key: 'countdown',
                className: 'text-3xl font-mono font-bold text-msi-chart-red mb-4'
            }, timeString),
            React.createElement('p', {
                key: 'info',
                className: 'text-sm text-gray-500'
            }, 'This restriction will remain active even if you refresh or reopen the application.')
        ]);
    };

    const updateCountdown = () => {
        if (remainingSeconds <= 0) {
            // Clear timer first
            if (rateLimitTimer) {
                clearInterval(rateLimitTimer);
                rateLimitTimer = null;
            }
            localStorage.removeItem(RATE_LIMIT_KEY);
            // Use setTimeout to avoid synchronous cleanup during render
            setTimeout(() => {
                hideRateLimitOverlay();

                // redirect to main website
                window.location.href = window.config.SEASCAPE_APP_URL;
            }, 0);
            return;
        }

        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        const timeString = minutes > 0
            ? `${minutes}:${seconds.toString().padStart(2, '0')}`
            : `${seconds} seconds`;

        // Update the existing root with new content, but only if root still exists
        if (rateLimitRoot) {
            try {
                rateLimitRoot.render(renderRateLimitContent(timeString));
            } catch (error) {
                // If render fails, stop the timer and cleanup
                console.warn('Error during countdown render:', error);
                if (rateLimitTimer) {
                    clearInterval(rateLimitTimer);
                    rateLimitTimer = null;
                }
                return;
            }
        }

        remainingSeconds--;
    };

    // Initial render
    updateCountdown();

    // Update every second
    rateLimitTimer = setInterval(updateCountdown, 1000);
};

// Hide rate limit overlay
export const hideRateLimitOverlay = () => {
    // Clear timer first to stop any ongoing renders
    if (rateLimitTimer) {
        clearInterval(rateLimitTimer);
        rateLimitTimer = null;
    }

    const existingOverlay = document.getElementById('rate-limit-overlay');
    if (existingOverlay) {
        if (rateLimitRoot) {
            // Use setTimeout to avoid synchronous unmounting during render
            setTimeout(() => {
                try {
                    if (rateLimitRoot) {
                        rateLimitRoot.unmount();
                        rateLimitRoot = null;
                    }
                } catch (error) {
                    // Ignore unmount errors during cleanup
                    console.warn('Error during rate limit overlay unmount:', error);
                    rateLimitRoot = null;
                }
                // Remove the DOM element after unmounting to avoid race conditions
                const overlay = document.getElementById('rate-limit-overlay');
                if (overlay) {
                    overlay.remove();
                }
            }, 0);
        } else {
            // If no React root, just remove the element
            existingOverlay.remove();
        }
    }
};

// Check for existing rate limit on page load (local storage only)
export const checkExistingRateLimit = () => {
    if (isRateLimited()) {
        const remainingSeconds = getRateLimitRemainingSeconds();
        if (remainingSeconds > 0) {
            // Only show overlay if it's not already visible
            const existingOverlay = document.getElementById('rate-limit-overlay');
            if (!existingOverlay || !rateLimitTimer) {
                showRateLimitOverlay();
            }
        } else {
            localStorage.removeItem(RATE_LIMIT_KEY);
        }
    }
};

// Initialize rate limit check when module loads
if (typeof window !== 'undefined') {
    // Check immediately when the module loads
    setTimeout(() => {
        checkExistingRateLimit();
    }, 0);

    // Also check when page becomes visible (in case user switched tabs)
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            // Add a small delay to ensure proper cleanup before checking
            setTimeout(() => {
                checkExistingRateLimit();
            }, 100);
        }
    });

    // Clean up timers and roots before page unload to prevent race conditions
    window.addEventListener('beforeunload', () => {
        if (rateLimitTimer) {
            clearInterval(rateLimitTimer);
            rateLimitTimer = null;
        }
        // Don't try to unmount during page unload - just clear references
        rateLimitRoot = null;
        overlayRoot = null;
    });
}